import {ReplaySubject, combineLatest, BehaviorSubject} from 'rxjs';
import {filter, map, shareReplay, startWith} from 'rxjs/operators';
import {arr2Map} from '@px/helper/structure-covert';
import {configApi} from '@px/api/config';
import {wwData$} from './worker-data';

const originalCurrencies$ = new BehaviorSubject<OriginalCurrencyEntity[]>([]); // products中原始currencies数据
export const currencies$ = new ReplaySubject<CurrencyEntity[]>(1); // 结合 currenciesBasic/currenciesInfo 中的数据
export const currenciesCache$ = currencies$.pipe(
  filter((o) => o.length > 0),
  map((o) => arr2Map(o, 'currency')),
  startWith(new Map()),
  shareReplay(1)
);
export const currenciesCacheByCode$ = currencies$.pipe(
  filter((o) => o.length > 0),
  map((o) => arr2Map(o, 'code')),
  startWith(new Map()),
  shareReplay(1)
);

export const contracts$ = new ReplaySubject<ContractPair[]>(1);
// 默认 taker 手续费比例，一般当作一次交易的手续费
export const contractDefaultTakerFeeRateEr$ = new BehaviorSubject<number>(0);
export const leverageMargins$ = new ReplaySubject<LeverageMargins[]>(1);
export const leverageMarginsMap$ = leverageMargins$.pipe(map((contracts) => arr2Map<LeverageMargins, number>(contracts, 'index_id')));
export const contractMap$ = contracts$.pipe(map((contracts) => arr2Map(contracts, 'symbol')));
export const spots$ = new ReplaySubject<SpotPair[]>(1);
export const spotsMap$ = spots$.pipe(map((spots) => arr2Map(spots, 'symbol')));
export const leverages$ = new ReplaySubject<Leverage[]>(1);
export const currenciesBasic$ = new ReplaySubject<CoinBasic[]>(1);
export const riskLimitCache$ = new ReplaySubject<Map<string, RiskLimit[]>>(1);
export const tradeLevelsCache$ = new BehaviorSubject<TradeLevels>({
  blackSymbols: [],
  levels: [],
});
export const products$ = new BehaviorSubject({contracts: [], spots: [], riskLimits: [], leverages: [], collaterals: [], marginRiskLevels: null, leverageMargins: []});
export const currenciesInfoMap$ = new ReplaySubject<Map<string, CoinInfo>>(1);
export const categories$ = new ReplaySubject<Categories[]>(1);
export const stSpotSymbolMaps$ = spots$.pipe(map(convertSTSymbolMaps), shareReplay(1));
// Margin
export const isActivateMarginAccount$ = new ReplaySubject<boolean>(1);
export const marginPairs$ = new ReplaySubject<MarginPair[]>(1);
export const marginsMap$ = marginPairs$.pipe(map((contracts) => arr2Map(contracts, 'symbol')));
export const marginCollaterals$ = new ReplaySubject<MarginCollateral[]>(1);
export const marginCurrencies$ = new ReplaySubject<MarginCurrencyEntity[]>(1);
export const marginRiskLevels$ = new ReplaySubject<MarginRiskLevels>(1);
// Lending
export const isActivateLendingAccount$ = new ReplaySubject<boolean>(1);

const privateProducts$ = new ReplaySubject<PrivateCoin>(1);

export async function loadProducts() {
  const {data, error} = await configApi.getProductList();
  if (data) {
    const {currencies, riskLimits, tradeLevels} = data;
    products$.next(data);
    originalCurrencies$.next(currencies);
    riskLimitCache$.next(riskLimits.reduce((p, c) => p.set(c.symbol, c.riskLimits), new Map()));
    tradeLevelsCache$.next(tradeLevels || {blackSymbols: [], levels: []});
  }
  if (error) {
    console.warn('getProducts error', error);
  }
}

export async function loadCoinBasic() {
  const {data, error} = await configApi.getCoinBasic();
  if (data) {
    currenciesBasic$.next(data.rows);
  }
  if (error) {
    console.warn('load coin basic list error', error);
  }
}

export async function loadCoinsInfo(lang) {
  const {data, error} = await configApi.getCoinInfoList({lang});
  if (data) {
    currenciesInfoMap$.next(arr2Map(data.rows, 'currency'));
  }
  if (error) {
    console.warn('load coin info list error', error);
  }
}

export async function loadPrivateProducts() {
  const {data, error} = await configApi.getPrivateCoin();
  if (data) {
    privateProducts$.next(data);
  }
  if (error) {
    privateProducts$.next({currencyList: [], spotSymbolList: [], contractSymbolList: []});
  }
}

export async function loadCategory(lang) {
  const {data} = await configApi.getCategory({lang});
  if (data) {
    categories$.next(data.rows);
  }
}

combineLatest([originalCurrencies$, currenciesBasic$])
  .pipe(map(([originalCurrencies, currenciesBasic]) => convertCurrencies(originalCurrencies, currenciesBasic)))
  .subscribe((currencies) => currencies$.next(currencies));

combineLatest([products$, currencies$, privateProducts$, categories$]).subscribe(([products, currencies, privateProducts, categories]) => {
  if (currencies.length === 0) {
    return;
  }
  const {contracts, spots, riskLimits, leverages, collaterals = [], marginRiskLevels, leverageMargins = []} = products;
  const {currencyList, contractSymbolList, spotSymbolList} = privateProducts;
  const currenciesData = currencies.filter((item) => currencyList.indexOf(item.currency) === -1);
  const contractsData = contracts
    .filter((item) => contractSymbolList.indexOf(item.symbol) === -1)
    .map((contract) => {
      const categoryIds = categories.filter((o) => o.coins.includes(contract.baseCurrency)).map((item) => item.id);
      return {...contract, categoryIds};
    });
  const collateralsData = collaterals.map((o) => convertCollateralOperationAuthority(o));
  const collateralTradeCurrencies = collateralsData.filter((o) => o.canTrade).map((o) => o.currency);
  const collateralBorrowCurrencies = collateralsData.filter((o) => o.canBorrow).map((o) => o.currency);
  const spotsData: SpotPair[] = spots
    .filter((item) => spotSymbolList.indexOf(item.symbol) === -1)
    .map((spot) => {
      const base: CurrencyEntity = currenciesData.find((o) => o.currency === spot.baseCurrency);
      const name = base ? base.name : '';
      const categoryIds = categories.filter((o) => o.coins.includes(spot.baseCurrency)).map((item) => item.id);
      const supportMarginTrade =
        spot.hasOwnProperty('leverage') && spot.leverage > 0 && collateralTradeCurrencies.includes(spot.baseCurrency) && collateralTradeCurrencies.includes(spot.quoteCurrency);
      return {
        ...spot,
        name,
        categoryIds,
        mode: 'Spot',
        supportMarginTrade,
        baseCurrencyCanBorrow: collateralBorrowCurrencies.includes(spot.baseCurrency),
        quoteCurrencyCanBorrow: collateralBorrowCurrencies.includes(spot.quoteCurrency),
      };
    });
  const marginPairsData: MarginPair[] = findMarginPairs(spotsData);
  const marginCurrenciesData = convertMarginCollaterals(collateralsData, currenciesData);
  const riskLimitsData = riskLimits.filter((item) => contractSymbolList.indexOf(item.symbol) === -1);

  wwData$.next({
    type: 'foundation-data',
    currencies: currenciesData,
    contracts: contractsData,
    spots: spotsData,
    riskLimits: riskLimitsData,
    leverages,
    marginCurrencies: marginCurrenciesData,
    marginPairs: marginPairsData,
    marginRiskLevels,
    leverageMargins,
  });
  contracts$.next(contractsData);
  leverageMargins$.next(leverageMargins);
  spots$.next(spotsData);
  leverages$.next(leverages);
  marginRiskLevels$.next(marginRiskLevels);
  marginCollaterals$.next(collateralsData);
  marginPairs$.next(marginPairsData);
  marginCurrencies$.next(marginCurrenciesData);
});

function convertCurrencies(originalCurrencies, currenciesBasic) {
  return originalCurrencies.map((item) => {
    const curBasicCurrency = currenciesBasic.find((o) => o.currency === item.currency) || {assetsPrecision: 8};
    item.valuePrecision = curBasicCurrency.assetsPrecision || 8;
    item.qtyPrecision = ['USD', 'USDT'].indexOf(item.currency) < 0 ? 8 : 2;
    item.tickSizeEv = item.currency === 'USD' ? 100 : 1;
    return item;
  });
}

function convertSTSymbolMaps(spots) {
  return spots.reduce((pre, spot) => {
    if (spot.stStatus === 1) {
      pre[spot.symbol] = spot;
    }
    return pre;
  }, {});
}

function findMarginPairs(spots: SpotPair[]) {
  return spots.reduce<MarginPair[]>((pre, spot) => {
    if (spot.supportMarginTrade) {
      const marginPair = {...spot, mode: 'Margin'} as MarginPair;
      pre.push(marginPair);
    }
    return pre;
  }, []);
}

function convertMarginCollaterals(marginCollaterals, currencies): MarginCurrencyEntity[] {
  return marginCollaterals.map((o) => {
    const currencyInfo = currencies.find((item) => item.currency === o.currency);
    if (!currencyInfo) {
      console.warn('not found config for ' + o.currency + ' currency in currencies list');
      return o;
    }
    return {
      ...o,
      name: currencyInfo.name,
      displayCurrency: currencyInfo.displayCurrency,
      valuePrecision: currencyInfo.valuePrecision,
      pricePrecision: currencyInfo.pricePrecision,
      status: currencyInfo.status,
      valueFactor: 1,
      valueScale: 0,
    };
  });
}

function convertCollateralOperationAuthority(collateral): MarginCollateral {
  /**
   * markDelete: boolean; // true means deleted, false means in the list
   * 资产不存在可借贷不可抵押情况
   * 资产下线后，不可借贷、不可交易、不可转入、可还款、可转出
   */
  const online = !collateral.markDelete;
  return {
    ...collateral,
    canTransferOut: true,
    canRepay: true,
    canTransferIn: online,
    canBorrow: online,
    canTrade: online,
  };
}
